import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  inject,
  OnInit,
  signal,
  untracked,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule } from '@ngx-translate/core';
import { CardService } from './services/payment-card/card.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { PlansCardsComponent } from '@app/pages/main-page/components/plans-banner/plans-cards/plans-cards.component';
import { IconComponent } from '@app/components/shared/icon/icon.component';
import { StatusModalComponent } from '@app/components/shared/status-modal/status-modal.component';
import { IconName } from '@app/utils/enums/icon-enum';
import { SubscriptionService } from './services/subscription/subscription.service';
import { SubscriptionResponse, SubscriptionUnit } from '@app/utils/interfaces/plans.interface';
import { ChangePaymentModalComponent } from '@app/pages/main-page/components/plans-banner/change-payment-modal/change-payment-modal.component';
import { CONFIRMATION_MODAL_CONFIGS } from '@app/utils/constants/modal.constants';
import { catchError, EMPTY, filter, finalize, switchMap, tap } from 'rxjs';
import { SnackbarService } from '@app/services/snackbar/snackbar.service';
import { SnackbarStatus } from '@app/utils/enums/snackbar.enum';
import { CardResponse, ConfirmModalResponse } from './interfaces/cards-response.interface';
import { CardExpiryPipe } from './payment/pipe/card-expiry-pipe.pipe';
import { ConfirmModalComponent } from '@app/components/shared/confirm-modal/confirm-modal.component';
import { CONFIRM_DIALOG_OPTIONS } from '@app/utils/constants/confirm.constants';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { PaymentItemModalComponent } from './modal-dialog/payment-item-modal/payment-item-modal/payment-item-modal.component';
import { ADD_PAYMENT_MODAL_SETTINGS } from '@app/utils/constants/add-payment-modal.constaints';
import { SubscribeStatesService } from './services/subscribe-state/subscribe-states.service';
import { PLANS } from '@app/utils/constants/plans.constants';

@Component({
  selector: 'app-settings-subscription',
  standalone: true,
  imports: [
    TranslateModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDividerModule,
    ReactiveFormsModule,
    PlansCardsComponent,
    IconComponent,
    CardExpiryPipe,
    MatProgressSpinner,
  ],
  templateUrl: './settings-subscription.component.html',
  styleUrl: './settings-subscription.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SettingsSubscriptionComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialog = inject(MatDialog);
  private readonly subService = inject(SubscriptionService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly subscribeState = inject(SubscribeStatesService);

  public plans = signal<SubscriptionUnit[]>([]);
  public cards = signal<CardResponse[]>([]);

  public selectedCardId = signal<number | null>(null);
  public iconName = IconName;
  public paymentMethoedsLoading = signal<boolean>(false);

  private readonly cardService = inject(CardService);

  constructor() {
    effect(() => {
      const trigger = this.subscribeState.cardsUpdated();

      if (trigger > 0) {
        untracked(() => {
          this.loadCards();
        });
      }
    });
  }

  public ngOnInit(): void {
    this.loadCards();
    this.loadSubPlan();
  }

  public onCardSelect(event: Event, cardId: number, stripePaymentMethodId: string): void {
    event.preventDefault();
    const dialogRef = this.dialog.open(ChangePaymentModalComponent, {
      data: {
        activeCardId: this.selectedCardId() ? this.selectedCardId()! : null,
        newActive: cardId,
      },
      ...CONFIRMATION_MODAL_CONFIGS,
    });

    dialogRef
      .afterClosed()
      .pipe(
        switchMap((confRes: ConfirmModalResponse) => {
          if (typeof confRes === 'number') {
            return this.cardService.updateSelectedCard(stripePaymentMethodId).pipe(
              tap(() => this.handleSuccess(confRes)),
              catchError(() => {
                this.handleFail();
                return EMPTY;
              }),
            );
          }
          if (confRes === false) {
            this.handleFail();
          }
          return EMPTY;
        }),

        catchError(() => {
          return EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private handleSuccess(id: number): void {
    this.selectedCardId.set(id);
    this.snackbarService.openSnackbar(
      SnackbarStatus.SUCCESS,
      'shop-settings.subscription.paymentMethod.status-messages.success',
    );
  }

  private handleFail() {
    this.snackbarService.openSnackbar(
      SnackbarStatus.FAIL,
      'shop-settings.subscription.paymentMethod.status-messages.fail',
    );
  }

  private openErrorModal(): void {
    this.dialog.open(StatusModalComponent, {
      data: {
        isSuccess: false,
        title: 'shop-settings.subscription.error.title',
        description: 'shop-settings.subscription.error.description',
      },
    });
  }

  public handleDeletePaymentMethod(stripePaymentMethodId: string): void {
    const isLastCard = this.cards().length === 1;

    const displayTitle = 'shop-settings.subscription.paymentMethod.delete-method.title';
    const desc = isLastCard
      ? 'shop-settings.subscription.paymentMethod.delete-method.last-card-desc'
      : 'shop-settings.subscription.paymentMethod.delete-method.desc';

    const dialogRef = this.dialog.open(ConfirmModalComponent, {
      ...CONFIRM_DIALOG_OPTIONS,
      data: {
        title: displayTitle,
        desc: desc,
      },
    });

    dialogRef
      .afterClosed()
      .pipe(
        filter((confirm) => {
          return !!confirm;
        }),
        tap(() => this.paymentMethoedsLoading.set(true)),
        switchMap(() => {
          return this.cardService.deletePaymentMethod(stripePaymentMethodId).pipe(
            tap(() => {
              if (isLastCard) {
                this.loadSubPlan();
              }

              this.subscribeState.notifyCardsUpdate();
            }),
            catchError(() => {
              this.paymentMethoedsLoading.set(false);
              this.handleFail();
              return EMPTY;
            }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private loadCards(): void {
    this.paymentMethoedsLoading.set(true);

    this.cardService
      .getCards()
      .pipe(
        tap((cards) => {
          this.cards.set(cards.data);
          const defaultCard = this.cards().find((card) => {
            return card.isDefault;
          });
          this.selectedCardId.set(defaultCard ? defaultCard.id : null);
          this.subService.defaultStipeMethod.set(
            defaultCard ? defaultCard.stripePaymentMethodId : '',
          );
        }),
        catchError(() => {
          this.cards.set([]);
          this.selectedCardId.set(null);
          this.subService.defaultStipeMethod.set('');
          this.openErrorModal();
          return EMPTY;
        }),
        finalize(() => {
          this.paymentMethoedsLoading.set(false);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  public openAddPaymentModal(): void {
    this.dialog.open(PaymentItemModalComponent, ADD_PAYMENT_MODAL_SETTINGS);
  }

  private loadSubPlan(): void {
  this.subService
    .getSubscribtionPlans()
    .pipe(
      tap((resPlans: SubscriptionResponse) => {
        const merged = resPlans.data.map((apiPlan) => {
          const mock = PLANS.find((p) => p.id === apiPlan.id);
          return {
            ...apiPlan,
            planFeature: apiPlan.planFeature?.length
              ? apiPlan.planFeature
              : (mock?.planFeature ?? []),
          };
        });

        this.plans.set(merged);

        const activePlan = merged.find((p) => p.isActive);
        if (activePlan) {
          this.subService.activePlanId.set(activePlan.id);
        }
      }),
      takeUntilDestroyed(this.destroyRef),
    )
    .subscribe();
}
}
